import React, { useEffect, useState } from "react";
import { Col, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import calender from "assets/images/icons/calendar.svg";
import {
  getMinMaxDate,
  getRelativeTimeFromNow,
  handleStopPropagation,
} from "components/common/Utils";
import { orderStatusTypes } from "components/constants/StatusKeys";
import {
  orderSpaceType,
  spaceTypeOptions,
} from "components/constants/DropdownOptions";
import NoDataFound from "components/common/NoDataFound";
import { ChevronRight } from "lucide-react";
import {
  doughnutChartCommonOptions,
  reviewDashboardChart,
} from "components/constants/ChartConstant";
import DoughnutChart from "components/common/Charts/DoughnutChart";
import { useDispatch, useSelector } from "react-redux";
import { getDashboardBookingDetails } from "store/actions/dashboardSlice";
import IsLoading from "components/common/IsLoading";
import DateRangePicker from "components/common/DateRangePicker";
import BranchDropdown from "../Overview/BranchDropdown";
import { MultiSelect } from "primereact/multiselect";

const CompletedAndCancellationBookings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading4, bookingDetails } = useSelector((state) => state.Dashboards);
  const { branchesWithCity } = useSelector((state) => state.Branches);

  const { positiveNegativeChart } = reviewDashboardChart;
  const [dates, setDates] = useState(null);
  const [spaceTypes, setSpaceTypes] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    getAllData();
  }, []);

  const getAllData = (action) => {
    const { actionType } = action || {}; // SEARCH, RESET, RESET_KEYWORD

    const isReset = actionType === "RESET";
    const payload = {
      spaceTypeInt: isReset ? [] : spaceTypes, // [] for all , 1 - workspace, 2- meetingroom, 3 - workshop
      branches: isReset ? [] : branches,
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };
    dispatch(getDashboardBookingDetails(payload));
  };

  const handleReset = () => {
    setDates([]);
    setBranches([]);
    setSpaceTypes([]);
    getAllData({ actionType: "RESET" });
  };

  const [data, setData] = useState({
    labels: [],
    datasets: [
      {
        data: [],
        ...positiveNegativeChart,
        backgroundColor: ["#FF6363", "#C4D26E"],
        hoverBackgroundColor: ["#FF6363", "#C4D26E"],
      },
    ],
  });

  useEffect(() => {
    const { statusBreakDown } = bookingDetails || {};
    if (statusBreakDown?.length) {
      const updatedLabels = statusBreakDown.map((item) => t(item?._id));
      const updatedData = statusBreakDown.map((item) => item.count);

      setData((prev) => ({
        ...prev,
        labels: updatedLabels,
        datasets: prev.datasets.map((dataset) => ({
          ...dataset,
          data: updatedData,
        })),
      }));
    }
  }, [bookingDetails, t]);

  const getTotalBookings = (statusBreakDown = []) => {
    return statusBreakDown.reduce(
      (total, item) => total + (item?.count || 0),
      0
    );
  };

  const getBookingStats = (statusBreakDown = [], type = "") => {
    const total = getTotalBookings(statusBreakDown);
    const target = statusBreakDown.find((item) => item._id === type);
    const count = target?.count || 0;
    const percentage = total > 0 ? ((count / total) * 100).toFixed(0) : 0;
    return { count, percentage };
  };

  const completed = getBookingStats(
    bookingDetails?.statusBreakDown,
    "Completed"
  );
  const cancelled = getBookingStats(
    bookingDetails?.statusBreakDown,
    "Cancelled"
  );

  const handleSelectionChange = (newSelected) => {
    setBranches(newSelected);
  };

  const handleBranchApply = () => {
    getAllData({ actionType: "SEARCH" });
  };

  return (
    <React.Fragment>
      <Col lg={12} className="">
        <div className="g_between gap-3">
          <h2 className="fs_20">{t("Cancellation")}</h2>
          <div className="d-flex gap-3 common_filter">
            <div className="buttons">
              <button
                className="search_btn"
                onClick={() => getAllData({ actionType: "SEARCH" })}
              >
                {t("Search")}
              </button>
              <button className="reset_btn" onClick={handleReset}>
                {t("Reset")}
              </button>
            </div>
            <MultiSelect
              value={spaceTypes}
              onChange={(e) => setSpaceTypes(e.value)}
              options={orderSpaceType}
              selectAllLabel={t("Select All")}
              optionLabel="name"
              placeholder={"Space Type"}
              maxSelectedLabels={3}
              className="filter_multi_select"
            />
            <BranchDropdown
              data={branchesWithCity}
              label={t("Branch")}
              allLabel={t("Choose All Branches")}
              resetLabel={t("Reset all")}
              onSelectionChange={handleSelectionChange}
              selectedData={branches}
              handleApply={handleBranchApply}
            />
            <DateRangePicker dates={dates} setDates={setDates} />
          </div>
        </div>
      </Col>
      {loading4 ? null : (
        <>
          <Col lg={12}>
            <div className="cancellation_div">
              <div className="g_between align-items-start top_heading">
                <h2 className="fs_20">{t("Completed & Cancelled Bookings")}</h2>
                <div>
                  <h2 className="fs_12">{t("Total Bookings")}</h2>
                  <p className="fs_12 mt-1">
                    {getTotalBookings(bookingDetails?.statusBreakDown)}
                  </p>
                </div>
              </div>
              <div className="inner_div g_center">
                {/* Cancelled Section */}
                <div className="text-center me-4" style={{ maxWidth: 70 }}>
                  <div className="d-flex align-items-start gap-1 justify-content-center mb-1">
                    <span
                      className="rounded-circle mt-1"
                      style={{
                        minWidth: 10,
                        minHeight: 10,
                        backgroundColor: "#FF6363",
                      }}
                    ></span>
                    <span className="text_dark_gray fs_12">
                      {t("Cancelled Bookings")}
                    </span>
                  </div>
                  <p className="fs_12 mt-1">
                    {cancelled.count} ({cancelled.percentage}%)
                  </p>
                </div>
                <div>
                  <DoughnutChart
                    data={data}
                    options={{
                      ...doughnutChartCommonOptions,
                      plugins: {
                        ...doughnutChartCommonOptions.plugins,
                        legend: {
                          ...doughnutChartCommonOptions.plugins.legend,
                          display: false, // 👈 hide legend only here
                        },
                      },
                    }}
                    width={200}
                    height={200}
                  />
                </div>

                {/* Completed Section */}
                <div className="text-center me-4" style={{ maxWidth: 70 }}>
                  <div className="d-flex align-items-start gap-1 justify-content-center mb-1">
                    <span
                      className="rounded-circle mt-1"
                      style={{
                        minWidth: 10,
                        minHeight: 10,
                        backgroundColor: "#C4D26E",
                      }}
                    ></span>
                    <span className="text_dark_gray fs_12">
                      {t("Completed Bookings")}
                    </span>
                  </div>
                  <p className="fs_12 mt-1">
                    {completed.count} ({completed.percentage}%)
                  </p>
                </div>
              </div>
            </div>
          </Col>
          <Col lg={12} className="my-3">
            <div className="common_table_style">
              {bookingDetails?.last5CancelledRecords?.length === 0 ? (
                <NoDataFound />
              ) : (
                <Table>
                  <thead className="t_head">
                    <tr>
                      <th>
                        <div className="d-flex align-items-center gap-1">
                          <img
                            src={calender}
                            alt="icon"
                            width={16}
                            height={16}
                          />
                          {t("Date")}
                        </div>
                      </th>
                      <th>{t("Status")}</th>
                      <th>{t("Booking No")}</th>
                      <th>{t("User")}</th>
                      <th>{t("Number")}</th>
                      <th>{t("Booking")}</th>
                      <th>{t("Reasons")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookingDetails?.last5CancelledRecords?.map((item) => (
                      <tr
                        key={item._id}
                        onClick={() =>
                          navigate(
                            `/bookings/bookings/booking-details?id=${item?._id}`
                          )
                        }
                        className="c_p"
                      >
                        <td>
                          <p className="w_fit_content">
                            {getRelativeTimeFromNow(item?.createdAt || "-")}
                          </p>
                        </td>
                        <td>
                          <p
                            className={`w_fit_content
                              ${
                                orderStatusTypes?.find(
                                  (option) =>
                                    option?.value === item?.bookingStatus
                                )?.class
                              }
                            `}
                          >
                            {t(
                              orderStatusTypes?.find(
                                (option) =>
                                  option?.value === item?.bookingStatus
                              )?.name
                            )}
                          </p>
                        </td>
                        <td>#{item?.bookingNo || "-"}</td>
                        <td onClick={handleStopPropagation}>
                          <Link
                            to={`/customers/customer-information?id=${item?.user?._id}`}
                            className="text_primary text-decoration-underline"
                          >
                            {item?.user?.name || "-"}
                          </Link>
                        </td>
                        <td>{item?.user?.phone || "-"}</td>
                        <td style={{ minWidth: 175 }}>
                          {t(
                            spaceTypeOptions?.find(
                              (option) => option.value === item?.spaceType
                            )?.name
                          )}{" "}
                          {"-"} {item?.space?.name}
                          <p className="fs_12 text_gray">
                            {item?.city?.name}, {item?.branch?.name}
                          </p>
                        </td>
                        <td>
                          <p className="fs_14 mt-2" style={{ minWidth: 200 }}>
                            {item?.cancelInfo?.cancelledNote || "-"}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
            <div className="d-flex justify-content-end mt-4">
              <button
                onClick={() => navigate("/bookings/bookings")}
                className="see_all transparent_btn"
              >
                {t("See All")} <ChevronRight />
              </button>
            </div>
          </Col>
        </>
      )}
    </React.Fragment>
  );
};

export default CompletedAndCancellationBookings;
